import { Box, FormControl, MenuItem, Select, Stack, Switch, Typography } from "@material-ui/core";
import { useTheme } from "@material-ui/core/styles";
import { WHATSAPP_MIN_RECENCY_MINUTES } from "../../../../@types/sharedSchema";
import { RECENCY_OPTIONS, RECENCY_SKIP_ON_MINUTES, isSkippingRecent } from "./campaignLabels";

type Props = {
  /** Minutes. WHATSAPP_MIN_RECENCY_MINUTES means "skip nobody" — the default everywhere. */
  value: number;
  onChange: (minutes: number) => void;
  disabled?: boolean;
};

/**
 * "לדלג על מי שקיבל ממני הודעה לאחרונה" — ONE control for every dialog that sends a template to
 * several people (the CRM's send, the campaign wizard, the listing share), so the choice reads the
 * same wherever it is made. Off by default (Haim, 2026-10-21): a test campaign skipped its only
 * recipient under the old 30-day default and nothing on the screen had said it would.
 *
 * Off still keeps the engine's 10-minute guard — a message sent to the person minutes ago is a
 * double send, not a choice — so the copy says "גם למי שקיבל ממך הודעה לאחרונה", not "to everyone".
 */
export default function RecencySkipControl({ value, onChange, disabled = false }: Props) {
  const theme = useTheme();
  const skipping = isSkippingRecent(value);
  const windows = RECENCY_OPTIONS.filter((option) => isSkippingRecent(option.minutes));

  return (
    <Box sx={{ p: 1.5, borderRadius: 1.5, border: `1px solid ${theme.palette.divider}` }}>
      <Stack direction="row" alignItems="center" spacing={1}>
        <Box sx={{ flexGrow: 1, minWidth: 0 }}>
          <Typography variant="subtitle2" component="label" htmlFor="recency-skip-switch" sx={{ display: "block", cursor: disabled ? "default" : "pointer" }}>
            לדלג על מי שקיבל ממני הודעה לאחרונה
          </Typography>
          <Typography variant="caption" sx={{ color: "text.secondary", display: "block" }}>
            {skipping
              ? "מי שקיבל ממך הודעה בתקופה שבחרת לא יקבל את ההודעה. אפשר לשלוח לו בכל זאת מעמוד הקמפיין."
              : "כבוי: ההודעה תישלח לכל מי שבחרת, גם למי שקיבל ממך הודעה לאחרונה."}
          </Typography>
        </Box>
        <Switch
          id="recency-skip-switch"
          checked={skipping}
          disabled={disabled}
          onChange={(event) => onChange(event.target.checked ? RECENCY_SKIP_ON_MINUTES : WHATSAPP_MIN_RECENCY_MINUTES)}
        />
      </Stack>
      {skipping && (
        <FormControl fullWidth size="small" sx={{ mt: 1.5 }}>
          <Select
            value={value}
            disabled={disabled}
            onChange={(event) => onChange(Number(event.target.value))}
            inputProps={{ "aria-label": "כמה זמן אחורה" }}
          >
            {windows.map((option) => (
              <MenuItem key={option.minutes} value={option.minutes}>
                מי שקיבל ממני הודעה {option.phrase}
              </MenuItem>
            ))}
          </Select>
        </FormControl>
      )}
    </Box>
  );
}
